import { FC, useCallback } from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  UseFormGetValues,
  UseFormReset,
  UseFormReturn,
} from 'react-hook-form';
import { AddonFormData, AddonTypeData, TOption } from '@src/types';
import { InputSelect, Button, Input } from '@features/ui';
import { DeleteIcon } from '@features/ui/icons';
import { ChooseAccountType } from '@features/admin/productsAddons/ChooseAccountType.tsx';

export const AddonForm: FC<
  UseFormReturn<AddonFormData> & { productOptions: TOption[] }
> = ({
  control,
  reset,
  getValues,
  productOptions,
  formState: { isSubmitting, errors },
}) => {
  const handleChangeAccount = useCallback(
    (
      field: ControllerRenderProps<AddonFormData, 'addon_packages'>,
      value: string
    ) => {
      const currentValues = field.value ?? [];

      const valueIndex = currentValues.indexOf(Number(value));

      if (valueIndex === -1) {
        field.onChange([...currentValues, Number(value)]);
      }
    },
    []
  );

  return (
    <div>
      <div className={'text-xs lg:w-[80%] w-full'}>
        <p className={'mb-3.5'}>Addon details</p>
        <div className={'flex w-full lg:flex-row flex-col gap-x-13 gap-y-7'}>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <div className={'flex flex-1 flex-col gap-3.5'}>
                <p>Title</p>
                <div className={'flex flex-col gap-1'}>
                  <Input
                    error={!!errors.title?.message}
                    placeholder={'Enter'}
                    textSize={'xs'}
                    variant={'secondary'}
                    {...field}
                  />
                  <p className={'mt-2.5'}>For your reference</p>
                  {errors.title?.message && (
                    <p className={'text-red-500'}>{errors.title?.message}</p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="price"
            render={({ field }) => (
              <div className={'flex flex-1 flex-col gap-3.5'}>
                <p>Price</p>
                <div className={'flex flex-col gap-1'}>
                  <Input
                    className={'lg:w-[70%] w-full'}
                    error={!!errors.price?.message}
                    placeholder={'$'}
                    textSize={'xs'}
                    type={'number'}
                    variant={'secondary'}
                    {...field}
                  />
                  {errors.title?.message && (
                    <p className={'text-red-500'}>{errors.price?.message}</p>
                  )}
                </div>
              </div>
            )}
          />
        </div>
        <div className={'flex w-full lg:flex-row flex-col gap-x-13 gap-y-7'}>
          <Controller
            control={control}
            name="addon_packages"
            render={({ field }) => {
              return (
                <div className={'flex-1'}>
                  <div className={'flex flex-col gap-3.5 mt-6 mb-[25px]'}>
                    <p>Account</p>
                    <InputSelect
                      className={'text-xs'}
                      options={productOptions.map((item) => ({
                        value: item.value.toString(),
                        label: item.label,
                      }))}
                      placeholder={'Please Select'}
                      onChange={(value) => handleChangeAccount(field, value)}
                    />
                  </div>
                  <div className={'flex flex-col gap-[13px]'}>
                    {field.value?.map((item) => {
                      const label = productOptions.find(
                        (it) => it.value === item
                      )?.label;

                      return (
                        <button
                          key={item}
                          className={
                            'cursor-pointer flex gap-[17px] items-center'
                          }
                          onClick={() =>
                            field.onChange(
                              field.value?.filter((it: string) => it !== item)
                            )
                          }
                        >
                          <DeleteIcon />
                          <p>{label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }}
          />
          <ChooseAccountType
            isEdit
            control={control as unknown as Control<AddonTypeData>}
            getValues={getValues as unknown as UseFormGetValues<AddonTypeData>}
            productOptions={productOptions}
            reset={reset as unknown as UseFormReset<AddonTypeData>}
          />
        </div>
      </div>
      <div className={'flex w-full justify-end mt-15 mb-7.5 pr-15'}>
        <Button
          loading={isSubmitting}
          size={'sm'}
          type={'submit'}
          variant={'lime'}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
